import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, MessageSquarePlus, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, fmtWhatsApp, formatApiError } from "@/lib/api";

const STATE_LABELS = {
  INICIO: "Início",
  MENU_PRINCIPAL: "Menu principal",
  ESCOLHENDO_CURSO: "Escolhendo curso",
  ESCOLHENDO_MODALIDADE: "Escolhendo modalidade",
  CONSULTANDO_HORARIOS: "Consultando horários",
  ESCOLHENDO_HORARIO: "Escolhendo horário",
  SUGERINDO_HORARIO: "Sugerindo horário",
  COLETANDO_DADOS: "Coletando dados",
  LEAD_CRIADO: "Lead criado",
  ATENDIMENTO_HUMANO: "Atendimento humano",
};

export default function WhatsAppSimulator() {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startForm, setStartForm] = useState({ phone: "", contact_name: "" });
  const [startError, setStartError] = useState("");
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const bottomRef = useRef(null);

  const loadConversations = async () => {
    try {
      const { data } = await api.get("/whatsapp/simulator/conversations");
      setConversations(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/whatsapp/simulator/conversations/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages?.length]);

  const select = async (id) => {
    setSelectedId(id);
    setSendError("");
    await loadDetail(id);
  };

  const start = async (e) => {
    e.preventDefault();
    setStartError("");
    setStarting(true);
    try {
      const { data } = await api.post("/whatsapp/simulator/conversations", {
        phone: startForm.phone,
        contact_name: startForm.contact_name.trim() || null,
      });
      setDialogOpen(false);
      setStartForm({ phone: "", contact_name: "" });
      await loadConversations();
      await select(data.id);
      toast.success("Conversa iniciada");
    } catch (err) {
      setStartError(formatApiError(err));
    } finally {
      setStarting(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedId || sending) return;
    setSending(true);
    setSendError("");
    try {
      await api.post(`/whatsapp/simulator/conversations/${selectedId}/messages`, { text: message.trim() });
      setMessage("");
      await loadDetail(selectedId);
      loadConversations();
    } catch (err) {
      setSendError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/whatsapp/simulator/conversations/${selectedId}/reset`);
      await loadDetail(selectedId);
      await loadConversations();
      toast.success("Conversa reiniciada para o estado inicial");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="wa-simulator-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter" data-testid="wa-simulator-title">
            Simulador de WhatsApp
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Teste o motor de conversação do WhatsApp Inteligente antes da integração oficial.
          </p>
        </div>
        <Badge variant="secondary" className="border border-primary/40 text-xs" data-testid="wa-dev-badge">
          Ferramenta interna — nenhuma mensagem é enviada ao WhatsApp real
        </Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="border border-border bg-white" data-testid="wa-conversations-panel">
          <div className="flex items-center justify-between border-b border-border p-3">
            <span className="text-sm font-semibold">Conversas</span>
            <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)} data-testid="wa-new-conversation-button">
              <MessageSquarePlus size={16} className="mr-1" /> Nova
            </Button>
          </div>
          <div className="max-h-[540px] overflow-y-auto">
            {conversations.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground" data-testid="wa-conversations-empty">
                Nenhuma conversa simulada ainda.
              </p>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => select(c.id)}
                data-testid={`wa-conversation-item-${c.phone}`}
                className={`w-full border-b border-border p-3 text-left transition-colors hover:bg-zinc-50 ${
                  selectedId === c.id ? "border-l-2 border-l-primary bg-zinc-50" : ""
                }`}
              >
                <p className="text-sm font-semibold">{c.contact_name || fmtWhatsApp(c.phone)}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {fmtWhatsApp(c.phone)} · {STATE_LABELS[c.state] || c.state}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col border border-border bg-white" data-testid="wa-chat-panel">
          {!detail ? (
            <div
              className="flex flex-1 items-center justify-center p-10 text-center text-sm text-muted-foreground"
              data-testid="wa-empty-state"
            >
              Inicie uma nova conversa ou selecione uma existente para simular o atendimento.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
                <div>
                  <p className="text-sm font-semibold" data-testid="wa-chat-phone">
                    {detail.conversation.contact_name || fmtWhatsApp(detail.conversation.phone)}
                  </p>
                  <div className="mt-1 flex gap-2">
                    <Badge variant="secondary" data-testid="wa-state-badge">
                      {STATE_LABELS[detail.conversation.state] || detail.conversation.state}
                    </Badge>
                    {detail.conversation.lead_id && (
                      <Badge className="bg-primary text-primary-foreground" data-testid="wa-lead-badge">
                        Lead vinculado
                      </Badge>
                    )}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={reset} data-testid="wa-reset-button">
                  <RotateCcw size={14} className="mr-1" /> Reiniciar
                </Button>
              </div>

              <div
                className="min-h-[320px] flex-1 space-y-3 overflow-y-auto bg-zinc-50 p-4"
                style={{ maxHeight: 480 }}
                data-testid="wa-messages"
              >
                {detail.messages.map((m, i) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] whitespace-pre-line border px-3 py-2 text-sm ${
                        m.direction === "out"
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-white"
                      }`}
                      data-testid={m.direction === "out" ? `wa-message-out-${i}` : `wa-message-in-${i}`}
                    >
                      {m.body}
                      {m.intent && m.direction === "in" && (
                        <span className="mt-1 block text-[10px] uppercase tracking-wider text-muted-foreground">
                          intenção: {m.intent}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {sendError && (
                <p
                  className="flex items-center gap-2 border-t border-destructive/50 bg-destructive/10 px-4 py-2 text-xs text-destructive"
                  data-testid="wa-send-error"
                >
                  <AlertTriangle size={14} /> {sendError}
                </p>
              )}
              <form onSubmit={send} className="flex gap-2 border-t border-border p-3" data-testid="wa-message-form">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Digite a mensagem do cliente..."
                  maxLength={1000}
                  data-testid="wa-message-input"
                />
                <Button
                  type="submit"
                  disabled={sending || !message.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="wa-send-button"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={16} />}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova conversa simulada</DialogTitle>
          </DialogHeader>
          <form onSubmit={start} className="space-y-4" data-testid="wa-start-form">
            {startError && (
              <div
                className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
                data-testid="wa-start-error"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{startError}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="wa-phone">Telefone do cliente (com DDD) *</Label>
              <Input
                id="wa-phone"
                required
                placeholder="(11) 99999-0000"
                value={startForm.phone}
                onChange={(e) => setStartForm((f) => ({ ...f, phone: e.target.value }))}
                data-testid="wa-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wa-name">Nome do contato (opcional)</Label>
              <Input
                id="wa-name"
                value={startForm.contact_name}
                onChange={(e) => setStartForm((f) => ({ ...f, contact_name: e.target.value }))}
                data-testid="wa-contact-name-input"
              />
            </div>
            <Button
              type="submit"
              disabled={starting}
              className="w-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
              data-testid="wa-start-button"
            >
              {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Iniciar conversa
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
